import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CreateIcon from '@mui/icons-material/Create';
import EditNoteIcon from '@mui/icons-material/EditNote';
import FeedbackIcon from '@mui/icons-material/Feedback';
import PaletteIcon from '@mui/icons-material/Palette';
import LockIcon from '@mui/icons-material/Lock';
import LogoutIcon from '@mui/icons-material/Logout';
import ImageIcon from '@mui/icons-material/Image';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SaveIcon from '@mui/icons-material/Save';
import StraightenIcon from '@mui/icons-material/Straighten';
import {
  CircularProgress,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Switch,
  Tooltip,
} from '@mui/material';

import { useWallStore, type BoardSetting, type WallBoard } from '@walls/store';
import { getPanelColor, getPanelTitleColor } from '../theme/wallThemeStyles';

interface BoardActionMenuProps {
  anchorEl: HTMLElement | null;
  board: WallBoard;
  onClose: () => void;
  onExportImage: () => void;
  onExportPdf: () => void;
  onFeedback: () => void;
  onEditTheme: () => void;
  onSaveTemplate: () => void;
  canSaveTemplates: boolean;
  exportInProgress?: boolean;
  imageExporting?: boolean;
  pdfExporting?: boolean;
  onLogOut: () => void;
}

const ownerSettings: Array<{
  icon: JSX.Element;
  label: string;
  setting: BoardSetting | 'elementLocking';
}> = [
  {
    icon: <CreateIcon fontSize="small" />,
    label: 'Drawing tools',
    setting: 'allowDrawingTools',
  },
  {
    icon: <EditNoteIcon fontSize="small" />,
    label: 'Card creation',
    setting: 'allowCardCreation',
  },
  {
    icon: <LockIcon fontSize="small" />,
    label: 'Element locking',
    setting: 'elementLocking',
  },
  {
    icon: <AutoFixHighIcon fontSize="small" />,
    label: 'Automatic aligning',
    setting: 'autoAlignCards',
  },
  {
    icon: <AutoFixHighIcon fontSize="small" />,
    label: 'AI tools',
    setting: 'allowAiTools',
  },
  {
    icon: (
      <svg
        height="30"
        viewBox="0 0 24 24"
        style={{ marginLeft: -6 }}
        fill="none"
        stroke="#000000"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.3"
      >
        <path d="m12 2.8 8 6-8 6-8-6z"></path>
        <path d="m4 13.2 8 6 8-6"></path>
      </svg>
    ),
    label: 'Card Grouping',
    setting: 'allowClustering',
  },
  {
    icon: <StraightenIcon fontSize="small" />,
    label: 'Card resizing',
    setting: 'allowCardResizing',
  },
];

export function BoardActionMenu({
  anchorEl,
  board,
  onClose,
  onExportImage,
  onExportPdf,
  onFeedback,
  onEditTheme,
  onSaveTemplate,
  canSaveTemplates,
  exportInProgress = false,
  imageExporting = false,
  pdfExporting = false,
  onLogOut,
}: BoardActionMenuProps) {
  const updateBoardSetting = useWallStore((state) => state.updateBoardSetting);
  const updateElementLocking = useWallStore((state) => state.updateElementLocking);
  const theme = useWallStore((state) => state.theme);
  const open = Boolean(anchorEl);
  const elementLockingEnabled = board.allowCardLocking && board.allowCardUnlocking;
  const panelColor = getPanelColor(theme);
  const panelTitleColor = getPanelTitleColor(theme);

  const toggleSetting = (setting: BoardSetting) => {
    void updateBoardSetting(setting, !board[setting]);
  };

  const toggleElementLocking = () => {
    void updateElementLocking(!elementLockingEnabled);
  };

  const isSettingEnabled = (setting: BoardSetting | 'elementLocking') =>
    setting === 'elementLocking' ? elementLockingEnabled : board[setting];

  const toggleOwnerSetting = (setting: BoardSetting | 'elementLocking') => {
    if (setting === 'elementLocking') {
      toggleElementLocking();
      return;
    }

    toggleSetting(setting);
  };

  const backToSpaces = () => {
    window.location.href = '/main';
  };

  const exportPdf = () => {
    onClose();
    onExportPdf();
  };

  const exportImage = () => {
    onClose();
    onExportImage();
  };

  const editTheme = () => {
    onClose();
    onEditTheme();
  };

  const saveTemplate = () => {
    if (!canSaveTemplates) {
      return;
    }

    onClose();
    onSaveTemplate();
  };

  return (
    <Menu
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      slotProps={{
        paper: { sx: { bgcolor: panelColor, color: panelTitleColor, minWidth: 280, mt: 1 } },
      }}
    >
      {board.isOwner &&
        ownerSettings.map(({ icon, label, setting }) => (
          <MenuItem
            key={setting}
            onClick={() => toggleOwnerSetting(setting)}
            aria-label={`${
              isSettingEnabled(setting) ? 'Disable' : 'Enable'
            } ${label.toLowerCase()}`}
          >
            <ListItemIcon>{icon}</ListItemIcon>
            <ListItemText primary={label} />
            <Switch
              checked={isSettingEnabled(setting)}
              edge="end"
              inputProps={{ 'aria-label': label, readOnly: true }}
              size="small"
            />
          </MenuItem>
        ))}
      {board.isOwner && (
        <>
          <Divider />
          <MenuItem onClick={exportPdf} disabled={exportInProgress}>
            <ListItemIcon>
              {pdfExporting ? (
                <CircularProgress size={18} />
              ) : (
                <PictureAsPdfIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText primary={pdfExporting ? 'Exporting PDF...' : 'Export as PDF'} />
          </MenuItem>
          <MenuItem onClick={exportImage} disabled={exportInProgress}>
            <ListItemIcon>
              {imageExporting ? <CircularProgress size={18} /> : <ImageIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText primary={imageExporting ? 'Exporting image...' : 'Export as Image'} />
          </MenuItem>
          <Tooltip
            title={canSaveTemplates ? '' : 'Available with a gold subscription'}
            placement="right"
          >
            <span style={{ display: 'block' }}>
              <MenuItem onClick={saveTemplate} disabled={!canSaveTemplates}>
                <ListItemIcon>
                  <SaveIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Save as Template" />
              </MenuItem>
            </span>
          </Tooltip>
        </>
      )}
      <Divider />
      {board.isOwner && (
        <MenuItem onClick={editTheme}>
          <ListItemIcon>
            <PaletteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Edit Theme" />
        </MenuItem>
      )}
      <MenuItem onClick={onFeedback}>
        <ListItemIcon>
          <FeedbackIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Feedback" />
      </MenuItem>
      {board.isOwner && (
        <MenuItem onClick={backToSpaces}>
          <ListItemIcon>
            <OpenInNewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Back to spaces" />
        </MenuItem>
      )}
      <MenuItem onClick={onLogOut}>
        <ListItemIcon>
          <LogoutIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Sign out" />
      </MenuItem>
    </Menu>
  );
}
